import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as snackbarActions from '../../../actions/snackbarActions';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMd } from '@fortawesome/free-solid-svg-icons/faUserMd';
import ReusableDoctorTabs, { doctorRoutes } from '../../Utils/Menu/ReusableDoctorTabs';
import { PatientInformation } from '../PatientInfo/PatientInformation';
import MainSnackbar from '../../Utils/Snackbar/SmsSnackbar';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '../../Utils/FormInputs/TextField';
import Label from '../../Utils/FormInputs/Label';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import Chip from '@material-ui/core/Chip';
import { surgeryService } from '../../../services/surgeryService';
import { URGENCY, PATHWAY, PATIENT_TYPE } from '../../SurgeryModule/shared/constants';
import Form from 'react-bootstrap/Form';
import { AutoCompleteControlled } from '../../Utils/FormInputs/AutoCompleteInput';
import axios from 'axios';
import { config } from '../../../Helpers/env';
import { Link } from 'react-router-dom';
import { ResizableTextarea } from '../../Utils/FormInputs/Textarea';
import { logoutErrorMessage } from '../../../Helpers/ErrorMessages';

const useStyles = makeStyles((theme) => ({
  formSection: {
    padding: 20,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 16,
    color: '#333',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: 8,
  },
  contextPanel: {
    padding: 16,
    backgroundColor: '#fafafa',
    borderRadius: 4,
  },
  contextTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: 8,
    color: '#555',
  },
  historyItem: {
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    '&:last-child': { borderBottom: 'none' },
  },
  historyDate: {
    fontSize: '0.75rem',
    color: '#999',
  },
  historyProcedure: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  historySurgeon: {
    fontSize: '0.75rem',
    color: '#666',
  },
  urgencyElective: {
    color: '#2196F3',
  },
  urgencyUrgent: {
    color: '#FF9800',
  },
  urgencyEmergency: {
    color: '#F44336',
  },
  emergencyBanner: {
    marginTop: 8,
    marginBottom: 8,
  },
  submitBtn: {
    marginTop: 16,
    backgroundColor: '#3f51b5',
    color: '#fff',
    '&:hover': { backgroundColor: '#303f9f' },
  },
  diagnosisChip: {
    margin: '2px 4px',
  },
  noHistory: {
    fontSize: '0.85rem',
    color: '#999',
    fontStyle: 'italic',
    padding: '12px 0',
  },
}));

/**
 * DoctorSurgeryRequest — Surgery referral tab in the doctor's consultation sidebar.
 * Replaces the old Surgery.js component. This is a full referral form (left 60%)
 * with patient surgery history context (right 40%).
 */
const DoctorSurgeryRequest = ({ actions, snackbars, match }) => {
  const classes = useStyles();
  const user = JSON.parse(sessionStorage.getItem('user'));
  const userObj = !user ? {} : user;
  const token = !userObj.token ? [] : userObj.token;
  const _token = !token[1] ? {} : token[1];
  const { patient_number, visit_id, patient_type, patient_admission_id } = match.params;
  const doctor_name = `${_token.first_name || ''} ${_token.last_name || ''}`;

  // Form state
  const [formData, setFormData] = useState({
    diagnosis: '',
    procedure: '',
    urgency: URGENCY.ELECTIVE,
    pathway: patient_type === '2' ? PATHWAY.INPATIENT : PATHWAY.OUTPATIENT,
    preferred_surgeon: '',
    clinical_notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Context panel state
  const [surgeryHistory, setSurgeryHistory] = useState([]);
  const [activeReferrals, setActiveReferrals] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  // Diagnosis check state (same pattern as DoctorAdmission)
  const [isDiagnosis, setIsDiagnosis] = useState(true); // true = no diagnosis yet
  const [diagnosisData, setDiagnosisData] = useState([]);
  const [diagnosisNotes, setDiagnosisNotes] = useState('');

  // Procedure services state
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);

  // Doctor tab routes
  const arr = doctorRoutes(patient_number, visit_id, patient_type, patient_admission_id);

  // Load patient surgery history and active referrals
  useEffect(() => {
    const fetchContext = async () => {
      try {
        const [historyRes, activeRes] = await Promise.all([
          surgeryService.getPatientSurgeryHistory(patient_number),
          surgeryService.getActiveReferrals(patient_number),
        ]);
        setSurgeryHistory(historyRes.data?.surgeries || []);
        setActiveReferrals(activeRes.data?.referrals || []);
      } catch (err) {
        console.error('Failed to load surgery context:', err);
      } finally {
        setLoadingHistory(false);
      }
    };

    const fetchSurgeryServices = async () => {
      try {
        const payload = new FormData();
        payload.append('service_type', 'Surgery');
        const res = await axios.post(`${config.smsUrl}/cvisit/retrieve_services_by_service_type`, payload);
        const details = res.data;
        const data = !details ? [] : details;
        const arr = data.map(item => ({
          value: `${item.service_id}-${item.cost}`,
          label: item.service_name
        }));
        setServices(arr);
      } catch (err) {
        console.error('Failed to load surgery services:', err);
      }
    };

    const fetchDoctors = async () => {
      try {
        const res = await surgeryService.searchDoctors({ query: '' });
        let docs = res.data?.doctors || res.data || [];
        const arr = docs.map(d => ({
          value: d.id,
          label: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim()
        }));
        setDoctors(arr);
      } catch (err) {
        console.error('Failed to fetch doctors:', err);
      }
    };

    fetchContext();
    fetchSurgeryServices();
    fetchDoctors();
  }, [patient_number]);

  // Check if diagnosis exists (same pattern as DoctorAdmission / useAddAdmission)
  useEffect(() => {
    if (!visit_id) {
      return;
    }
    const formData = new FormData();
    formData.append('visit_id', visit_id);
    axios.post(`${config.smsUrl}/cdoctor/get_all_patient_treatment_details`, formData)
      .then(res => {
        const data = res.data;
        const details = !data ? {} : data;
        const diagArr = details?.diagnosis || [];
        const noDiagnosis = diagArr.length <= 0;
        setIsDiagnosis(noDiagnosis);
        setDiagnosisData(diagArr);

        if (!noDiagnosis) {
          // Populate diagnosis field with comma-separated diagnosis names
          const diagnosisText = diagArr.map(d => {
            const code = d.code || d.icd_code || '';
            const name = d.diagnosis || d.diagnosis_name || '';
            return code ? `${code} — ${name}` : name;
          }).filter(Boolean).join(', ');
          setFormData(prev => ({ ...prev, diagnosis: diagnosisText }));

          // Collect diagnosis notes
          const notes = details?.diagnosis_notes || diagArr.map(d => d.notes).filter(Boolean).join('; ');
          setDiagnosisNotes(notes || '');
        }
      })
      .catch(err => {
        logoutErrorMessage(err, null, actions);
      });
  }, [visit_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProcedureChange = (selected) => {
    setFormData((prev) => ({ ...prev, procedure: selected }));
  };

  const handleSurgeonChange = (selected) => {
    setFormData((prev) => ({ ...prev, preferred_surgeon: selected }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Basic validation
    if (!formData.diagnosis || !formData.procedure || !formData.urgency) {
      actions.snackbarActions.snackError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await surgeryService.createReferral({
        patient_number,
        visit_id,
        patient_type,
        referring_doctor_id: _token.user_id,
        ...formData,
        procedure: formData.procedure ? formData.procedure.label : formData.procedure,
        procedure_id: formData.procedure ? formData.procedure.value?.split('-')[0] : null,
        preferred_surgeon: formData.preferred_surgeon ? formData.preferred_surgeon.label : formData.preferred_surgeon,
        preferred_surgeon_id: formData.preferred_surgeon ? formData.preferred_surgeon.value : null,
      });
      actions.snackbarActions.snackSuccess('Surgery referral submitted successfully');
      // Reset form (keep diagnosis since it's auto-populated)
      setFormData(prev => ({
        ...prev,
        procedure: '',
        urgency: URGENCY.ELECTIVE,
        pathway: patient_type === '2' ? PATHWAY.INPATIENT : PATHWAY.OUTPATIENT,
        preferred_surgeon: '',
        clinical_notes: '',
      }));
      setSubmitted(false);
      // Refresh active referrals
      const activeRes = await surgeryService.getActiveReferrals(patient_number);
      setActiveReferrals(activeRes.data?.referrals || []);
    } catch (err) {
      actions.snackbarActions.snackError('Failed to submit surgery referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { openBar, type, message } = snackbars;

  return (
    <div className="journals">
      <PageTitle title="Surgery Request" />
      <SubHeader title="Doctor" subTitle="Surgery Request" doctor_name={doctor_name}>
        <FontAwesomeIcon icon={faUserMd} />
      </SubHeader>

      <MainSnackbar variant={type} open={openBar} message={message}
        handleCloseBar={() => actions.snackbarActions.hideSnackbar()} />

      <ReusableDoctorTabs components={arr} patient_type={+patient_type}>
        <PatientInformation {...{ patient_number, visit_id }} />

        {isDiagnosis ? (
          <div className="mt-2 px-3">
            <Alert severity="error" style={{ paddingTop: 0, paddingBottom: 0, marginBottom: '8px' }}>
              <span style={{ display: 'inline-block', marginRight: 4 }}>
                Before making a surgery request, please add the patient's diagnosis
              </span>
              <Link to={{ pathname: `/doctordiagnosis/${patient_number}/${visit_id}/${patient_type}/null` }}>
                Add diagnosis
              </Link>
            </Alert>
          </div>
        ) : null}

        <Grid container spacing={3} style={{ padding: '16px 20px', opacity: isDiagnosis ? 0.5 : 1, pointerEvents: isDiagnosis ? 'none' : 'auto' }}>
          {/* Left Panel — Referral Form (60%) */}
          <Grid item xs={12} md={7}>
            <Paper className={classes.formSection} elevation={1}>
              <Typography className={classes.sectionTitle}>Surgery Request Form</Typography>

              <form onSubmit={handleSubmit} autoComplete="off">
                {/* Clinical Diagnosis (auto-populated from diagnosis module) */}
                <Form.Group className="mb-3 mt-3">
                  <Label name="Clinical Diagnosis *" />
                  <ResizableTextarea
                    placeholder="Diagnosis will be populated from the diagnosis module"
                    value={formData.diagnosis}
                    name="diagnosis"
                    rows="2"
                    disabled
                  />
                </Form.Group>

                {/* Diagnosis Notes (from diagnosis module) */}
                {diagnosisNotes && (
                  <Form.Group className="mb-3">
                    <Label name="Diagnosis Notes" />
                    <ResizableTextarea
                      value={diagnosisNotes}
                      name="diagnosis_notes"
                      rows="2"
                      disabled
                    />
                  </Form.Group>
                )}

                {/* Recommended Procedure */}
                <Form.Group className="mb-3">
                  <Label name="Recommended Procedure *" />
                  <AutoCompleteControlled
                    options={services}
                    handleChange={handleProcedureChange}
                    submitted={submitted}
                    medicine_value={formData.procedure}
                    placeholder="Select procedure..."
                    id="procedure"
                    value_text="value"
                    label_text="label"
                  />
                  {submitted && !formData.procedure && (
                    <small className="text-danger d-block mt-1">Procedure is required</small>
                  )}
                </Form.Group>

                {/* Urgency */}
                <Form.Group className="mb-3">
                  <Label name="Urgency" />
                  <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                    <Form.Check
                      type="radio"
                      id="urgency-elective"
                      name="urgency"
                      value={URGENCY.ELECTIVE}
                      label="Elective"
                      checked={formData.urgency === URGENCY.ELECTIVE}
                      onChange={handleChange}
                      className={classes.urgencyElective}
                    />
                    <Form.Check
                      type="radio"
                      id="urgency-urgent"
                      name="urgency"
                      value={URGENCY.URGENT}
                      label="Urgent"
                      checked={formData.urgency === URGENCY.URGENT}
                      onChange={handleChange}
                      className={classes.urgencyUrgent}
                    />
                    <Form.Check
                      type="radio"
                      id="urgency-emergency"
                      name="urgency"
                      value={URGENCY.EMERGENCY}
                      label="Emergency"
                      checked={formData.urgency === URGENCY.EMERGENCY}
                      onChange={handleChange}
                      className={classes.urgencyEmergency}
                    />
                  </div>
                </Form.Group>

                {formData.urgency === URGENCY.EMERGENCY && (
                  <Alert severity="error" className={classes.emergencyBanner}>
                    Emergency referrals will be flagged for immediate theatre allocation
                  </Alert>
                )}

                {/* Pathway */}
                <Form.Group className="mb-3">
                  <Label name="Pathway" />
                  <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                    <Form.Check
                      type="radio"
                      id="pathway-outpatient"
                      name="pathway"
                      value={PATHWAY.OUTPATIENT}
                      label="Outpatient (Day-Case)"
                      checked={formData.pathway === PATHWAY.OUTPATIENT}
                      onChange={handleChange}
                      disabled={patient_type === '2'} // locked for inpatients
                    />
                    <Form.Check
                      type="radio"
                      id="pathway-inpatient"
                      name="pathway"
                      value={PATHWAY.INPATIENT}
                      label="Inpatient"
                      checked={formData.pathway === PATHWAY.INPATIENT}
                      onChange={handleChange}
                    />
                  </div>
                  {patient_type === '2' && (
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px' }}>
                      Pathway locked to Inpatient for admitted patients
                    </Typography>
                  )}
                </Form.Group>

                {/* Preferred Surgeon (optional) */}
                <Form.Group className="mb-3">
                  <Label name="Preferred Surgeon (optional)" />
                  <AutoCompleteControlled
                    options={doctors}
                    handleChange={handleSurgeonChange}
                    submitted={submitted}
                    medicine_value={formData.preferred_surgeon}
                    placeholder="Start typing surgeon name..."
                    id="preferred_surgeon"
                    value_text="value"
                    label_text="label"
                  />
                </Form.Group>

                {/* Clinical Notes */}
                <Form.Group className="mb-3">
                  <Label name="Notes for Surgeon" />
                  <TextField
                    as="textarea"
                    placeholder="Clinical details relevant to the surgical team..."
                    value={formData.clinical_notes}
                    name="clinical_notes"
                    onChange={handleChange}
                    rows={3}
                  />
                </Form.Group>

                <button
                  type="submit"
                  className='btn sms-btn'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Surgery Request'}
                </button>
              </form>
            </Paper>
          </Grid>

          {/* Right Panel — Context & History (40%) */}
          <Grid item xs={12} md={5}>
            {/* Active Referral Warning */}
            {activeReferrals.length > 0 && (
              <Alert severity="warning" style={{ marginBottom: 16 }}>
                This patient has {activeReferrals.length} active surgery referral(s):
                {activeReferrals.map((ref) => (
                  <Chip
                    key={ref.id}
                    label={ref.procedure_requested}
                    size="small"
                    className={classes.diagnosisChip}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Alert>
            )}

            {/* Previous Surgeries */}
            <Paper className={classes.contextPanel} elevation={0}>
              <Typography className={classes.contextTitle}>Previous Surgeries</Typography>
              {loadingHistory ? (
                <Typography className={classes.noHistory}>Loading history...</Typography>
              ) : surgeryHistory.length === 0 ? (
                <Typography className={classes.noHistory}>No previous surgeries on record</Typography>
              ) : (
                surgeryHistory.map((s, i) => (
                  <div key={i} className={classes.historyItem}>
                    <Typography className={classes.historyDate}>
                      {s.operation_date || s.date}
                    </Typography>
                    <Typography className={classes.historyProcedure}>
                      {s.surgery_name || s.procedure}
                    </Typography>
                    <Typography className={classes.historySurgeon}>
                      {s.lead_surgeon_name || s.surgeon} — {s.status_label || 'Completed'}
                    </Typography>
                  </div>
                ))
              )}
            </Paper>

          </Grid>
        </Grid>
      </ReusableDoctorTabs>
    </div>
  );
};

function mapStateToProps(state) {
  return { snackbars: state.snackbar };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: {
      snackbarActions: bindActionCreators(snackbarActions, dispatch),
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DoctorSurgeryRequest);
